import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PageSEO } from "@/components/SEO";

const AboutPage = () => {
  return (
    <div data-testid="about-page">
      <PageSEO page="about" />
      
      {/* Hero Section */}
      <section className="section-luxury pt-32" data-testid="about-hero">
        <div className="container-luxury">
          <div className="max-w-4xl">
            <p className="text-overline mb-6">Our Story</p>
            <h1 className="text-display mb-8">
              The Essence of<br />Ikara Scents
            </h1>
            <div className="divider-luxury" />
          </div>
        </div>
      </section>

      {/* Brand Story */}
      <section className="pb-24" data-testid="brand-story">
        <div className="container-luxury">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            <div>
              <p className="text-subtitle mb-8">
                Born from a passion for perfumery and a vision to transform how spaces 
                are experienced, Ikara Scents emerged as a pioneer in artisanal space 
                scenting. From the house of Meilleur, we carry forward a legacy of 
                excellence in fragrance creation.
              </p>
              <p className="text-subtitle mb-8">
                Our journey began with a simple observation: while interiors are 
                meticulously designed with visual aesthetics in mind, the olfactory 
                dimension often remains unexplored. We set out to change this, 
                creating fragrances that become an invisible yet powerful element 
                of spatial design.
              </p>
              <p className="text-subtitle">
                Today, our scents grace the finest hotels, boutiques, restaurants, 
                spas, and residences across India and beyond. Each fragrance is a 
                carefully orchestrated composition, designed to evoke specific 
                emotions and create lasting impressions.
              </p>
            </div>
            <div 
              className="aspect-square"
              style={{ backgroundColor: 'var(--surface)' }}
            >
              <img
                src="https://images.unsplash.com/photo-1730402224803-7e17932be9d4?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                alt="Ikara Scents artisanal fragrance creation process - premium space scenting India"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section 
        className="section-luxury"
        style={{ backgroundColor: 'var(--surface)' }}
        data-testid="philosophy-section"
      >
        <div className="container-luxury">
          <div className="text-center mb-16">
            <p className="text-overline mb-6">Our Philosophy</p>
            <h2 className="text-display text-3xl sm:text-4xl lg:text-5xl">
              Three Pillars of Excellence
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 lg:gap-16">
            <div className="text-center" data-testid="pillar-1">
              <span 
                className="font-heading text-6xl lg:text-7xl font-light block mb-6"
                style={{ color: 'var(--accent)' }}
              >
                01
              </span>
              <h3 className="font-heading text-2xl font-light mb-4">Artisanal Craft</h3>
              <p className="text-subtitle">
                Each fragrance is hand-composed by our master perfumers, 
                using the finest ingredients sourced from around the world. 
                We believe in the art of slow creation.
              </p>
            </div>

            <div className="text-center" data-testid="pillar-2">
              <span 
                className="font-heading text-6xl lg:text-7xl font-light block mb-6"
                style={{ color: 'var(--accent)' }}
              >
                02
              </span>
              <h3 className="font-heading text-2xl font-light mb-4">Bespoke Solutions</h3>
              <p className="text-subtitle">
                We understand that every space is unique. Our consultative 
                approach ensures that the fragrance we recommend perfectly 
                aligns with your brand and spatial requirements.
              </p>
            </div>

            <div className="text-center" data-testid="pillar-3">
              <span 
                className="font-heading text-6xl lg:text-7xl font-light block mb-6"
                style={{ color: 'var(--accent)' }}
              >
                03
              </span>
              <h3 className="font-heading text-2xl font-light mb-4">Precision Technology</h3>
              <p className="text-subtitle">
                Our diffuser machines are engineered for whisper-quiet operation 
                and consistent fragrance delivery, ensuring your space maintains 
                the perfect scent intensity at all times.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Made in India Section */}
      <section className="section-luxury" data-testid="made-in-india">
        <div className="container-luxury">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-overline mb-6">Heritage</p>
            <h2 className="text-display text-3xl sm:text-4xl lg:text-5xl mb-8">
              Proudly Made in India
            </h2>
            <div className="divider-luxury mx-auto" />
            <p className="text-subtitle mb-8">
              India has a rich heritage in fragrance that spans millennia. 
              From the ancient practice of attar-making to the modern 
              perfumery industry, our country has always understood the 
              profound connection between scent and emotion.
            </p>
            <p className="text-subtitle mb-8">
              At Ikara Scents, we honour this heritage while embracing 
              contemporary techniques. Our fragrances are crafted in our 
              atelier, where traditional knowledge meets modern 
              innovation. We source many of our ingredients from Indian 
              growers, supporting local communities while ensuring the 
              highest quality.
            </p>
            <p className="text-subtitle">
              Every Ikara fragrance carries within it the essence of India—
              warmth, complexity, and an undeniable depth that resonates 
              across cultures and spaces. 100% Vegan. 100% Organic.
            </p>
          </div>
        </div>
      </section>

      {/* The Meilleur Connection */}
      <section 
        className="section-luxury"
        style={{ backgroundColor: 'var(--surface)' }}
        data-testid="meilleur-section"
      >
        <div className="container-luxury">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-overline mb-6">From the House of</p>
              <h2 className="text-display text-3xl sm:text-4xl lg:text-5xl mb-8">
                Meilleur
              </h2>
              <div className="divider-luxury" />
              <p className="text-subtitle mb-8">
                Meilleur, meaning "better" in French, embodies our relentless 
                pursuit of excellence. As part of the Meilleur family, Ikara 
                Scents benefits from decades of expertise in luxury goods and 
                a deep understanding of what discerning clients expect.
              </p>
              <p className="text-subtitle">
                This heritage shapes every aspect of our work—from the selection 
                of raw materials to the design of our diffuser machines, from 
                our client relationships to our after-sales service. We don't 
                just sell fragrances; we create olfactory partnerships built 
                on trust and excellence.
              </p>
            </div>
            <div className="flex items-center justify-center">
              <div 
                className="w-64 h-64 rounded-full flex items-center justify-center"
                style={{ backgroundColor: 'var(--background)' }}
              >
                <span 
                  className="font-heading text-4xl italic"
                  style={{ color: 'var(--secondary-text)' }}
                >
                  Meilleur
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-luxury text-center" data-testid="about-cta">
        <div className="container-luxury">
          <p className="text-overline mb-6">Experience Ikara</p>
          <h2 className="text-display text-3xl sm:text-4xl lg:text-5xl mb-8">
            Begin Your Olfactory Journey
          </h2>
          <p className="text-subtitle max-w-2xl mx-auto mb-12">
            Whether you're seeking to enhance a hospitality space, create 
            a signature retail experience, or bring luxury scenting to your 
            home, we're here to guide you.
          </p>
          <Link 
            to="/contact" 
            className="btn-luxury inline-flex items-center gap-2"
            data-testid="about-contact-btn"
          >
            Enquire Now
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
